/**
 * Single source of truth for SafeCurves modules and layers, modelled on
 * `nsw-rlgsp/layerConfig.ts`.
 *
 * Every per-layer concern (map style, z-order, WFS query properties,
 * attributes-panel layout, table tab, legend symbology) lives on that layer's
 * entry in `SAFECURVES_MODULES`; `moduleTable` / `attributesColumns` / `tableTabs`
 * and the active legend's sections derive from it, and `buildSystemLayers`
 * (`layers.ts`) walks it to build the `ISafeCurvesSystemLayers` envelope.
 *
 * Table keys are the `EIdentify`/`EPrioritize`/`EInterventions` enum VALUES,
 * written as string literals (not enum references): they double as GeoServer
 * layer titles. The `satisfies` clause catches a key drifting from its enum value.
 */

import type { ILayerSource } from '#lib/types.js';
import type { EIdentify, EPrioritize, EInterventions, ESafeCurvesModule } from './interfaces';
import {
	attributesColumnsOf,
	tableTabsOf,
	type LayerEntry,
	type ModuleEntry
} from '#lib/common/moduleTable.js';
import { roadSealHints } from '#lib/components/map/legend/symbology.js';
import { SIGN_LEVELS, signSpeed } from './legends/signImages';

import styleIdentify from './layer_styles/safecurves_identify_style.json';
import stylePrioritize from './layer_styles/safecurves_prioritize_style.json';
import styleInterventionsSigns from './layer_styles/safecurves_interventions_curve-advisory-signs_style.json';
import styleInterventionsSignsHalo from './layer_styles/safecurves_interventions_curve-advisory-signs-halo_style.json';
import styleInterventionsSpeedPlaques from './layer_styles/safecurves_interventions_curve-advisory-speeds_style.json';
import styleInterventionsPassingSightDistance from './layer_styles/safecurves_interventions_passing-sight-distance_style.json';
import styleInterventionsPffa from './layer_styles/safecurves_interventions_pavement-friction-focus-areas_style.json';
import styleInterventionsSpeedReviews from './layer_styles/safecurves_interventions_speed-limit-reviews_style.json';
import styleInterventionsSsd from './layer_styles/safecurves_interventions_stopping-sight-distance_style.json';
import styleInterventionsSsdObstructions from './layer_styles/safecurves_interventions_ssd-obstructions_style.json';
import styleInterventionsClearzone from './layer_styles/safecurves_interventions_clearzone_style.json';

import {
	attributesIdentifyCurves,
	attributesPrioritizeCorridors,
	attributesInterventionsSignage,
	attributesInterventionsSpeeds,
	attributesPavementFrictionFocusAreas,
	attributesSpeedLimitReview,
	attributesStoppingSightDistance,
	attributesSSDObstruction,
	attributesPassingSightDistance,
	attributesClearZone,
	tableColumnsByTab
} from './columns';

// Signs, their halo, and their speed plaques all draw from the one GeoServer
// feature titled this: the enum's literal value, copied rather than imported
// (table keys are string literals, see the module doc comment).
const SIGN_FEATURE_TITLE = 'Advisory speeds and signage';

const SAFECURVES_MODULES = {
	Identify: {
		label: 'Identify',
		layers: {
			Identify: {
				// GeoServer titles this feature "SafeCurves Identify", not the "Identify" table key.
				featureTitle: 'SafeCurves Identify',
				map: { style: styleIdentify, zIndex: 3 },
				attributes: attributesIdentifyCurves,
				legend: {
					// Colour shows curve context; road seal is a separate dash dimension.
					symbology: { extraItems: roadSealHints([0.8, 1]) }
				},
				table: {
					tabId: 'Curves',
					label: 'Curves',
					rowLabel: 'curve',
					columns: tableColumnsByTab.Curves
				}
			}
		}
	},
	Prioritize: {
		label: 'Prioritize',
		layers: {
			// Generalised low-zoom corridors from a separate tileset; non-selectable and folded into the Prioritize row.
			// Reuses the Prioritize paint but renders only below the detailed layer's minzoom, handing off to it there.
			'SafeCurves Prioritize (simplified)': {
				map: {
					style: { ...stylePrioritize, minzoom: 0, maxzoom: stylePrioritize.minzoom },
					zIndex: 0,
					selectable: false
				},
				// Reused for the type; never surfaces since the layer is not selectable.
				attributes: attributesPrioritizeCorridors,
				legend: { partOf: 'Prioritize', hideSymbology: true }
			},
			Prioritize: {
				// GeoServer titles this feature "SafeCurves Prioritize", not the "Prioritize" table key.
				featureTitle: 'SafeCurves Prioritize',
				map: { style: stylePrioritize, zIndex: 0 },
				attributes: attributesPrioritizeCorridors,
				legend: {
					// Colour shows priority; road seal is a separate dash dimension.
					symbology: { extraItems: roadSealHints([2, 1]) }
				},
				table: {
					tabId: 'Corridors',
					label: 'Corridors',
					rowLabel: 'corridor',
					columns: tableColumnsByTab.Corridors
				}
			}
		}
	},
	Interventions: {
		label: 'Interventions',
		layers: {
			[SIGN_FEATURE_TITLE]: {
				map: { style: styleInterventionsSigns, zIndex: 6 },
				attributes: attributesInterventionsSignage,
				table: {
					tabId: 'Signage',
					label: 'Signage',
					rowLabel: 'sign',
					columns: tableColumnsByTab.Signage
				},
				// Signs draw from a per-feature sprite id, not an enumerable ramp, so the
				// recommendation-level PNGs are supplied as explicit items.
				legend: {
					symbology: {
						items: SIGN_LEVELS.map(([label, image]) => ({
							label,
							swatch: { kind: 'icon', image },
							matches: (p) => p.advisory_sign_recommendation === label
						}))
					}
				}
			},
			'Advisory signs halo': {
				featureTitle: SIGN_FEATURE_TITLE,
				map: { style: styleInterventionsSignsHalo, zIndex: 5, selectable: false },
				// Not selectable, so this layout never actually surfaces; shares Signs'
				// layout since every layer entry requires one.
				attributes: attributesInterventionsSignage,
				// Renders the same sign icons as a tinted SDF halo, so it folds into the
				// Signs row with no symbology of its own.
				legend: { partOf: SIGN_FEATURE_TITLE, hideSymbology: true }
			},
			'Curve Advisory speeds': {
				featureTitle: SIGN_FEATURE_TITLE,
				map: { style: styleInterventionsSpeedPlaques, zIndex: 4 },
				attributes: attributesInterventionsSpeeds,
				// Plaques accompany the signs, so they fold into the Signs row as one
				// advisory-speed swatch, shown wherever a plaque value is present.
				legend: {
					partOf: SIGN_FEATURE_TITLE,
					symbology: {
						items: [
							{
								label: 'Advisory speed',
								swatch: { kind: 'icon', image: signSpeed },
								matches: (p) => p.advisory_plaque_value != null
							}
						]
					}
				}
			},
			'Passing Sight Distance': {
				map: { style: styleInterventionsPassingSightDistance, zIndex: 0 },
				attributes: attributesPassingSightDistance
			},
			'Pavement Friction Focus Areas': {
				map: { style: styleInterventionsPffa, zIndex: 0 },
				attributes: attributesPavementFrictionFocusAreas
			},
			'Speed Limit Review': {
				map: { style: styleInterventionsSpeedReviews, zIndex: 0 },
				attributes: attributesSpeedLimitReview
			},
			'Stopping Sight Distance': {
				map: { style: styleInterventionsSsd, zIndex: 0 },
				attributes: attributesStoppingSightDistance
			},
			'Stopping Sight Distance obstruction': {
				map: { style: styleInterventionsSsdObstructions, zIndex: 0 },
				attributes: attributesSSDObstruction
			},
			'Clear Zone': {
				map: { style: styleInterventionsClearzone, zIndex: 0 },
				attributes: attributesClearZone
			}
		}
	}
} satisfies Record<`${ESafeCurvesModule}`, ModuleEntry> & {
	Identify: { layers: Record<`${EIdentify}`, LayerEntry> };
	Prioritize: { layers: Record<`${EPrioritize}`, LayerEntry> };
	Interventions: { layers: Record<`${EInterventions}`, LayerEntry> };
};

type ModuleTable = typeof SAFECURVES_MODULES;

type SafeCurvesModuleId = keyof ModuleTable & string;

/** `module → layerId → ILayerSource | null` envelope, module itself nullable
 *  (Identify/Prioritize/Interventions can each be absent from the feed). */
export type ISafeCurvesSystemLayers = {
	[M in SafeCurvesModuleId]:
		| {
				[L in keyof ModuleTable[M]['layers']]: ILayerSource | null;
		  }
		| null;
};

// ---------------------------------------------------------------------------
// Derived AppConfig pieces: never edited when layers change.
// ---------------------------------------------------------------------------

// The wide view of the table, for iteration: `Object.entries` walks need the
// index-signature shape, not the literal keys.
export const moduleTable: Record<string, ModuleEntry> = SAFECURVES_MODULES;

export const attributesColumns = attributesColumnsOf(moduleTable);

export const tableTabs = tableTabsOf<ISafeCurvesSystemLayers>(moduleTable);
